import { useState } from 'react';
import Button from '../../../shared/components/Button';
import { Input } from '../../../shared/components/Input';
import { useCreateFeatureFlag, useUpdateFeatureFlag } from '../hooks/useFeatureFlags';
import { validateFlagDraft, variantShare } from '../rollout';
import {
  FeatureFlagRolloutType,
  FeatureFlagScope,
  FeatureFlagType,
  type FeatureFlag,
  type FeatureFlagVariant,
} from '../types';

interface FeatureFlagModalProps {
  /** Absent = create. Present = edit, and identity fields become read-only. */
  flag?: FeatureFlag;
  onClose: () => void;
}

const inputClass =
  'w-full px-3 border border-[#D9D9D9] bg-muted text-foreground rounded-[10px] h-[30px] text-[14px] focus:outline-none';

/**
 * Create/edit a flag.
 *
 * `key`, `type`, `scope` and `scopeValue` are shown but disabled when editing:
 * the server's UpdateFeatureFlagDto does not accept them, so an editable field
 * would look like it worked and change nothing. (key, scope, scopeValue) is also
 * the unique index — it is identity, not configuration.
 *
 * Uses native <select> rather than the shared <Select>, which needs a
 * DropdownProvider that App.tsx still does not mount (PE-2216) and blanks the
 * whole app outside the ~7 subtrees that wrap their own.
 */
export const FeatureFlagModal = ({ flag, onClose }: FeatureFlagModalProps) => {
  const isEdit = !!flag;
  const createMutation = useCreateFeatureFlag();
  const updateMutation = useUpdateFeatureFlag();

  const [key, setKey] = useState(flag?.key ?? '');
  const [description, setDescription] = useState(flag?.description ?? '');
  const [type, setType] = useState<FeatureFlagType>(flag?.type ?? FeatureFlagType.BOOLEAN);
  const [scope, setScope] = useState<FeatureFlagScope>(flag?.scope ?? FeatureFlagScope.GLOBAL);
  const [scopeValue, setScopeValue] = useState(flag?.scopeValue ?? '');
  const [enabled, setEnabled] = useState(flag?.enabled ?? false);
  const [rolloutType, setRolloutType] = useState<FeatureFlagRolloutType>(
    flag?.rolloutType ?? FeatureFlagRolloutType.ALL,
  );
  const [rolloutPercentage, setRolloutPercentage] = useState(
    flag?.rolloutPercentage != null ? String(flag.rolloutPercentage) : '',
  );
  const [allowList, setAllowList] = useState((flag?.allowListMerchantIds ?? []).join('\n'));
  const [variants, setVariants] = useState<FeatureFlagVariant[]>(flag?.variants ?? []);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const allowListIds = allowList
    .split(/[\n,]/)
    .map((id) => id.trim())
    .filter(Boolean);
  const percentage = rolloutPercentage === '' ? undefined : Number(rolloutPercentage);
  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = async () => {
    const found = validateFlagDraft({
      key,
      type,
      scope,
      scopeValue,
      rolloutType,
      rolloutPercentage: percentage,
      allowListMerchantIds: allowListIds,
      variants,
      requireKey: !isEdit,
    });
    setErrors(found);
    if (Object.keys(found).length) return;

    // Only send the fields each rollout/type actually uses. Sending a stale
    // percentage alongside an ALLOW_LIST rollout would be stored and then shown
    // back on the next edit as if it meant something.
    const shared = {
      description: description.trim() || undefined,
      enabled,
      rolloutType,
      ...(rolloutType === FeatureFlagRolloutType.PERCENTAGE ? { rolloutPercentage: percentage } : {}),
      ...(rolloutType === FeatureFlagRolloutType.ALLOW_LIST
        ? { allowListMerchantIds: allowListIds }
        : {}),
      ...(type === FeatureFlagType.MULTIVARIATE ? { variants } : {}),
    };

    if (isEdit) {
      await updateMutation.mutateAsync({ id: flag.id, payload: shared });
    } else {
      await createMutation.mutateAsync({
        key: key.trim(),
        type,
        scope,
        ...(scope === FeatureFlagScope.GLOBAL ? {} : { scopeValue: scopeValue.trim() }),
        ...shared,
      });
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-bold text-foreground">
          {isEdit ? 'Edit feature flag' : 'New feature flag'}
        </h2>

        <div className="space-y-1">
          <Input
            name="key"
            label="Key"
            value={key}
            disabled={isEdit}
            onChange={(e) => setKey(e.target.value)}
            placeholder="new-dashboard"
          />
          {errors.key && <p className="text-xs text-destructive">{errors.key}</p>}
        </div>

        <Input
          name="description"
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What this flag controls"
        />

        <div className="grid grid-cols-2 gap-3">
          <label className="block text-[14px] font-[600] text-foreground">
            Type
            <select
              className={`${inputClass} mt-1`}
              value={type}
              disabled={isEdit}
              onChange={(e) => setType(e.target.value as FeatureFlagType)}>
              <option value={FeatureFlagType.BOOLEAN}>Boolean</option>
              <option value={FeatureFlagType.MULTIVARIATE}>Multivariate</option>
            </select>
          </label>

          <label className="block text-[14px] font-[600] text-foreground">
            Scope
            <select
              className={`${inputClass} mt-1`}
              value={scope}
              disabled={isEdit}
              onChange={(e) => {
                const next = e.target.value as FeatureFlagScope;
                setScope(next);
                // A MERCHANT-scoped row must roll out to ALL; snap it so the
                // form can't sit in a state the server will reject.
                if (next === FeatureFlagScope.MERCHANT) setRolloutType(FeatureFlagRolloutType.ALL);
              }}>
              <option value={FeatureFlagScope.GLOBAL}>Global</option>
              <option value={FeatureFlagScope.SOLUTION}>Solution</option>
              <option value={FeatureFlagScope.MERCHANT}>Merchant</option>
            </select>
          </label>
        </div>

        {scope !== FeatureFlagScope.GLOBAL && (
          <div className="space-y-1">
            <Input
              name="scopeValue"
              label={scope === FeatureFlagScope.SOLUTION ? 'Solution slug' : 'Merchant id'}
              value={scopeValue}
              disabled={isEdit}
              onChange={(e) => setScopeValue(e.target.value)}
              placeholder={scope === FeatureFlagScope.SOLUTION ? 'rack-and-box' : 'merchant uuid'}
            />
            {errors.scopeValue && <p className="text-xs text-destructive">{errors.scopeValue}</p>}
          </div>
        )}

        <div className="space-y-1">
          <label className="block text-[14px] font-[600] text-foreground">
            Rollout
            <select
              className={`${inputClass} mt-1`}
              value={rolloutType}
              disabled={scope === FeatureFlagScope.MERCHANT}
              onChange={(e) => setRolloutType(e.target.value as FeatureFlagRolloutType)}>
              <option value={FeatureFlagRolloutType.ALL}>Everyone in scope</option>
              <option value={FeatureFlagRolloutType.PERCENTAGE}>Percentage of merchants</option>
              <option value={FeatureFlagRolloutType.ALLOW_LIST}>Specific merchants</option>
            </select>
          </label>
          {scope === FeatureFlagScope.MERCHANT && (
            <p className="text-xs text-muted-foreground">
              A merchant-scoped flag already names one merchant, so it always rolls out to all.
            </p>
          )}
          {errors.rolloutType && <p className="text-xs text-destructive">{errors.rolloutType}</p>}
        </div>

        {rolloutType === FeatureFlagRolloutType.PERCENTAGE && (
          <div className="space-y-1">
            <Input
              name="rolloutPercentage"
              label="Percentage"
              type="number"
              min={0}
              max={100}
              value={rolloutPercentage}
              onChange={(e) => setRolloutPercentage(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Bucketed per merchant, so the same merchant always lands the same side. Evaluations
              without a merchant context return false.
            </p>
            {errors.rolloutPercentage && (
              <p className="text-xs text-destructive">{errors.rolloutPercentage}</p>
            )}
          </div>
        )}

        {rolloutType === FeatureFlagRolloutType.ALLOW_LIST && (
          <div className="space-y-1">
            <label className="block text-[14px] font-[600] text-foreground">
              Merchant ids
              <textarea
                className="w-full mt-1 px-3 py-2 border border-[#D9D9D9] bg-muted text-foreground rounded-[10px] text-[14px] focus:outline-none"
                rows={4}
                value={allowList}
                onChange={(e) => setAllowList(e.target.value)}
                placeholder="One id per line"
              />
            </label>
            <p className="text-xs text-muted-foreground">
              {allowListIds.length} id{allowListIds.length === 1 ? '' : 's'}. Evaluations without a
              merchant context return false.
            </p>
            {errors.allowListMerchantIds && (
              <p className="text-xs text-destructive">{errors.allowListMerchantIds}</p>
            )}
          </div>
        )}

        {type === FeatureFlagType.MULTIVARIATE && (
          <div className="space-y-2">
            <p className="text-[14px] font-[600] text-foreground">Variants</p>
            {variants.map((variant, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  className={inputClass}
                  value={variant.key}
                  placeholder="treatment-a"
                  onChange={(e) =>
                    setVariants(
                      variants.map((v, i) => (i === index ? { ...v, key: e.target.value } : v)),
                    )
                  }
                />
                <input
                  className={`${inputClass} w-24`}
                  type="number"
                  min={0}
                  value={variant.weight}
                  onChange={(e) =>
                    setVariants(
                      variants.map((v, i) =>
                        i === index ? { ...v, weight: Number(e.target.value) } : v,
                      ),
                    )
                  }
                />
                {/* Weights are relative, not percentages — the server picks by
                    weighted random over the raw values, so show the share. */}
                <span className="text-xs text-muted-foreground w-14 text-right">
                  {variantShare(variants, index)}%
                </span>
                <button
                  type="button"
                  onClick={() => setVariants(variants.filter((_, i) => i !== index))}
                  className="text-sm text-red-600 hover:underline">
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setVariants([...variants, { key: '', weight: 50 }])}
              className="text-sm text-blue-600 hover:underline">
              Add variant
            </button>
            {errors.variants && <p className="text-xs text-destructive">{errors.variants}</p>}
          </div>
        )}

        <label className="flex items-center gap-2 text-sm text-foreground">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Enabled
        </label>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={isPending}>
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </div>
      </div>
    </div>
  );
};
